import {
  Box,
  Circle,
  Flex,
  HStack,
  Modal,
  ModalContent,
  ModalOverlay,
  Spacer,
  Text,
  VStack,
  Badge,
  Spinner,
  useToast,
} from '@chakra-ui/react';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { CloseIcon, OpenIcon } from '../../constants/IconData';
import { DownloadIcon } from '@chakra-ui/icons';
import DocViewer, { DocViewerRenderers } from '@cyntler/react-doc-viewer';
import { FILE_TYPE } from '../../constants/constants';
import { AuditReportContent, AUDIT_REPORT_MIME } from './AuditReportViewer';
import {
  canPreviewDocument,
  buildDocumentDownloadName,
  downloadDocument,
  normalizeDocumentFileType,
  openDocumentViewer,
  usesOnlineOfficePreview,
} from './documentViewer.utils';
import {
  DocumentPreviewErrorBoundary,
  DocumentPreviewFallback,
  OfficePreviewNotice,
} from './DocumentPreviewFallback';

const getFileBadge = (fileType) => {
  if (fileType === FILE_TYPE.CLIENT)   return { label: 'Client',   color: 'blue'   };
  if (fileType === FILE_TYPE.AUDIT)    return { label: 'Audited',  color: 'green'  };
  if (fileType === FILE_TYPE.ADDITIONAL) return { label: 'Additional', color: 'purple' };
  if (fileType === 'AUDIT_REPORT')     return { label: 'Report',   color: 'teal'   };
  return null;
};

const createFileEntry = (fileSource, displayFileName, fileMimeType, namePrefix, uriKey, fileType) => {
  if (!fileSource?.[uriKey]) return null;
  const uri = fileSource[uriKey];
  const safeDisplayFileName = String(displayFileName || 'Document');
  const cleanedName = `${namePrefix}${safeDisplayFileName.split('_').join(' ')}`;
  return {
    name:       cleanedName,
    uri,
    fileType:   normalizeDocumentFileType(fileMimeType, safeDisplayFileName),
    fileName:   safeDisplayFileName,
    mimeType:   fileMimeType,
    size:       fileSource.size,
    uploadedAt: fileSource.uploaded_at,
    uploader:   fileSource.uploaded_by
      ? `${fileSource.uploaded_by.first_name} ${fileSource.uploaded_by.last_name}`
      : null,
    type: fileType,
  };
};

const buildFilesList = (currentFile, attachments = []) => {
  const files = [];

  // Original file
  const originalEntry = createFileEntry(
    currentFile, currentFile?.name, currentFile?.mime_type, '', 'preview_url', FILE_TYPE.CLIENT
  );
  if (originalEntry) files.push(originalEntry);

  // Attachments — audit report gets special handling, others go through createFileEntry
  attachments.filter(Boolean).forEach((attachment) => {
    const f = attachment?.file;

    if (f?.mime_type === AUDIT_REPORT_MIME) {
      // Forward live state refs directly — DocViewer is bypassed for this type
      files.push({
        name:     f.name,
        uri:      f.url,
        fileType: AUDIT_REPORT_MIME,
        mimeType: AUDIT_REPORT_MIME,
        type:     attachment.attachment_type,
        _auditReportData:    f._auditReportData,
        _auditReportLoading: f._auditReportLoading,
        _auditReportError:   f._auditReportError,
        _auditReportRefresh: f._auditReportRefresh,
      });
      return;
    }

    const attachmentEntry = createFileEntry(
      f, f.name, f.mime_type, '', 'url', attachment.attachment_type
    );
    if (attachmentEntry) files.push(attachmentEntry);
  });

  // Audited version
  const auditedEntry = createFileEntry(
    currentFile,
    currentFile?.audited_chart_name,
    currentFile?.audited_chart_mime_type,
    '',
    'audited_chart_preview_url',
    FILE_TYPE.AUDIT
  );
  if (auditedEntry) files.push(auditedEntry);

  return files;
};

const FileViewer = ({
  isOpen,
  onClose,
  currentFile,
  attachments = [],
  viewerPath = null,
  chartId = null,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const toast = useToast();

  const allFiles = useMemo(() => {
    if (!isOpen) return [];
    return buildFilesList(currentFile, attachments);
  }, [isOpen, currentFile, attachments]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [isOpen]);

  const selectedFile = useMemo(() => allFiles[selectedIndex], [allFiles, selectedIndex]);

  // Only build docs array for non-audit-report files
  const docs = useMemo(() => {
    if (!selectedFile || selectedFile.mimeType === AUDIT_REPORT_MIME) return [];
    return [{ uri: selectedFile.uri, fileType: selectedFile.fileType }];
  }, [selectedFile]);

  const handleFileSelect  = useCallback((index) => setSelectedIndex(index), []);
  const handleOpenViewer = useCallback(() => {
    if (!openDocumentViewer(viewerPath)) {
      toast({
        title: 'Pop-up blocked',
        description: 'Allow pop-ups for this site to open the document viewer in a separate window.',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [toast, viewerPath]);

  const handleDownload = useCallback(async () => {
    if (!selectedFile?.uri || selectedFile.mimeType === AUDIT_REPORT_MIME) return;

    setIsDownloading(true);
    try {
      await downloadDocument({
        uri: selectedFile.uri,
        fileName: buildDocumentDownloadName(
          selectedFile.fileName || selectedFile.name,
          chartId
        ),
      });
    } finally {
      setIsDownloading(false);
    }
  }, [chartId, selectedFile]);

  const isAuditReport = selectedFile?.mimeType === AUDIT_REPORT_MIME;
  const isPreviewSupported = canPreviewDocument(selectedFile?.fileType, DocViewerRenderers);
  const isOnlineOfficePreview = usesOnlineOfficePreview(selectedFile?.fileType);
  const previewFallback = (
    <DocumentPreviewFallback
      file={selectedFile}
      onDownload={handleDownload}
      isDownloading={isDownloading}
    />
  );

  return (
    <Modal size="full" onClose={onClose} isOpen={isOpen}>
      <ModalOverlay bg="#001A41" opacity={0.8} />
      <ModalContent
        bg="white"
        maxW="90vw"
        maxH="100vh"
        minH="100vh"
        borderRadius="lg"
        overflow="hidden"
        boxShadow="2xl"
        display="flex"
        flexDirection="column"
      >
        {/* Header */}
        <HStack px={4} py={3} borderBottom="1px" borderColor="gray.200" bg="white" flexShrink={0}>
          <Text fontSize="lg" fontWeight="semibold" color="gray.800" noOfLines={1}>
            {selectedFile?.name || 'File Viewer'}
          </Text>
          <Spacer />
          {/* The pop-out opens the complete viewer, including generated reports. */}
          {viewerPath && (
            <Circle
              size="36px"
              bg="blue.50"
              color="blue.600"
              onClick={handleOpenViewer}
              cursor="pointer"
              _hover={{ bg: 'blue.100' }}
              transition="all 0.2s"
              title="Open complete viewer in a new window"
              role="button"
              aria-label="Open complete viewer in a new window"
            >
              <OpenIcon />
            </Circle>
          )}
          {selectedFile && !isAuditReport && (
            <Circle
              size="36px"
              bg="green.50"
              color="green.600"
              onClick={isDownloading ? undefined : handleDownload}
              cursor={isDownloading ? 'wait' : 'pointer'}
              _hover={isDownloading ? {} : { bg: 'green.100' }}
              transition="all 0.2s"
              title="Download selected document"
              role="button"
              aria-label="Download selected document"
            >
              {isDownloading ? <Spinner size="xs" /> : <DownloadIcon />}
            </Circle>
          )}
          <Circle
            size="36px"
            bg="gray.100"
            color="gray.600"
            cursor="pointer"
            onClick={onClose}
            _hover={{ bg: 'gray.200' }}
            transition="all 0.2s"
            title="Close"
          >
            <CloseIcon height="16px" width="16px" />
          </Circle>
        </HStack>

        <Flex flex="1" overflow="hidden" minH="0">
          {/* Sidebar */}
          <VStack
            w="280px"
            bg="gray.50"
            p={3}
            spacing={2}
            align="stretch"
            borderRight="1px"
            borderColor="gray.200"
            overflowY="auto"
            flexShrink={0}
          >
            <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={1}>
              FILES ({allFiles.length})
            </Text>
            {allFiles.map((file, index) => {
              const isSelected = selectedIndex === index;
              const badge = getFileBadge(file.type);

              return (
                <Box
                  key={index}
                  p={3}
                  bg={isSelected ? 'blue.50' : 'white'}
                  border="1px"
                  borderColor={isSelected ? 'blue.200' : 'gray.200'}
                  borderRadius="md"
                  cursor="pointer"
                  onClick={() => handleFileSelect(index)}
                  _hover={{
                    bg: isSelected ? 'blue.50' : 'gray.50',
                    borderColor: isSelected ? 'blue.300' : 'gray.300',
                  }}
                  transition="all 0.2s"
                >
                  <Text
                    fontWeight={isSelected ? 'semibold' : 'medium'}
                    fontSize="sm"
                    color="gray.700"
                    noOfLines={2}
                    mb={badge ? 1 : 0}
                  >
                    {file.name}
                  </Text>
                  {badge && (
                    <Badge colorScheme={badge.color} fontSize="xs">{badge.label}</Badge>
                  )}
                </Box>
              );
            })}
          </VStack>

          {/* Main Viewer */}
          <Box
            flex="1"
            bg={isAuditReport ? 'white' : 'gray.100'}
            position="relative"
            display="flex"
            flexDirection="column"
            minHeight="0"
            sx={{
              '& #react-doc-viewer': { height: '100% !important', display: 'flex', flexDirection: 'column' },
              '& #proxy-renderer':   { height: '100% !important', display: 'flex', flexDirection: 'column' },
              '& #msdoc-renderer':   { height: '100% !important', display: 'flex', flexDirection: 'column' },
              '& #msdoc-iframe':     { height: '100% !important', minHeight: '100vh' },
              '& iframe':            { height: '100% !important' },
            }}
          >
            {isAuditReport ? (
              // ── Audit report: render directly, bypass DocViewer ──────────
              <Box flex="1" overflow="auto" minHeight="0">
                <AuditReportContent
                  data={selectedFile._auditReportData}
                  loading={selectedFile._auditReportLoading}
                  error={selectedFile._auditReportError}
                  onRefresh={selectedFile._auditReportRefresh}
                />
              </Box>
            ) : docs.length > 0 && isPreviewSupported ? (
              // ── Normal files: use DocViewer ──────────────────────────────
              <Box p={2} flex="1" overflow="auto" minHeight="0" display="flex" flexDirection="column">
                {isOnlineOfficePreview && (
                  <OfficePreviewNotice key={selectedFile?.uri} />
                )}
                <Box flex="1" minHeight="0">
                  <DocumentPreviewErrorBoundary
                    resetKey={`${selectedIndex}-${selectedFile?.uri}`}
                    fallback={previewFallback}
                  >
                    <DocViewer
                      key={selectedIndex}
                      documents={docs}
                      pluginRenderers={DocViewerRenderers}
                      config={{
                        pdfZoom: { defaultZoom: 1.0, zoomJump: 0.2 },
                        header: { disableHeader: true, disableFileName: false, retainURLParams: false },
                        pdfVerticalScrollByDefault: true,
                        csvDelimiter: ',',
                        noRenderer: { overrideComponent: () => previewFallback },
                      }}
                      style={{ borderRadius: '8px' }}
                    />
                  </DocumentPreviewErrorBoundary>
                </Box>
              </Box>
            ) : docs.length > 0 ? (
              previewFallback
            ) : (
              <Flex h="100%" align="center" justify="center" direction="column" color="gray.500">
                <Text>Select a file to view</Text>
              </Flex>
            )}
          </Box>
        </Flex>
      </ModalContent>
    </Modal>
  );
};

export default FileViewer;
